/**
 * One-tap MARK (Field UX spec §8): no dialog, no category picker, no confirmation.
 *
 *  - The timestamp is taken at the PRESS (server-clock estimate) — never at release or when the
 *    network answers — so latency never moves it. PATCH never touches `t`.
 *  - Feedback: haptic tap + the MARK button flashes "M-14 · 10:42:07:12" for 1.5 s, with an Undo
 *    chip for 5 s.
 *  - A second press within 400 ms upgrades that mark to a Hero mark (category 'hero') instead of
 *    creating a second one.
 *  - Categorising / labelling happens after the flight (marker list edit form, Replay).
 * A failed post offers a retry with the same timestamp.
 */
import { MarkOutbox, isRetryable, newClientId } from './markOutbox';
import { useCallback } from 'react';
import { create } from 'zustand';
import type { EpochMs, Id, LatLon, Marker } from '@apos/shared';
import { formatUtc, timeOfDayTimecode } from '@apos/shared';
import { api, errorMessage, type MarkerInput } from '../../lib/api';
import { serverNow } from '../../lib/clock';
import { toast } from '../../components/ui';
import { useMission } from '../../state/mission';
import { classifyPress, MARK_FLASH_MS, nextMarkNumber, UNDO_WINDOW_MS } from './markerModel';

export interface MarkContext {
  position: LatLon | null;
  aircraftId: Id | null;
  cameraId: Id | null;
}

export interface MarkFeedback {
  /** increments per press, so the button can re-trigger its flash */
  seq: number;
  label: string;
  /** time-of-day timecode at the press (mission-local), e.g. "10:42:07:12" */
  tc: string;
  hero: boolean;
  flashUntil: number;
  undoUntil: number;
  /** resolves to the stored marker (null if the post failed) */
  pending: Promise<Marker | null>;
}

interface MarkFeedbackState {
  last: MarkFeedback | null;
  set(f: MarkFeedback | null): void;
  patch(p: Partial<MarkFeedback>): void;
}

export const useMarkFeedback = create<MarkFeedbackState>((set, get) => ({
  last: null,
  set: (last) => set({ last }),
  patch: (p) => {
    const cur = get().last;
    if (cur) set({ last: { ...cur, ...p } });
  },
}));

function haptic(pattern: number | number[] = 30): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported */
  }
}

export function upsertMarkerLocally(m: Marker): void {
  useMission.getState().ingest([{ type: 'marker', op: 'upsert', data: m }]);
}

/** Marks that could not reach the server wait here (persisted) and sync automatically. */
export const markOutbox = new MarkOutbox(undefined, (m) => upsertMarkerLocally(m));
if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') markOutbox.start();

async function post(missionId: Id, input: MarkerInput): Promise<Marker | null> {
  const withId = { ...input, clientId: input.clientId ?? newClientId() };
  try {
    const m = await api.markers.create(missionId, withId);
    upsertMarkerLocally(m);
    return m;
  } catch (e) {
    if (isRetryable(e)) {
      markOutbox.enqueue(missionId, withId);
      toast({ title: `MARK ${formatUtc(withId.t!)} SAVED ON THIS PHONE`, body: 'No connection — it will sync automatically when signal returns.', tone: 'neutral' });
      return null;
    }
    toast({
      title: `MARK ${formatUtc(input.t!)} NOT SAVED`,
      body: errorMessage(e),
      tone: 'alert',
      timeoutMs: 0,
      action: { label: 'Retry', onClick: () => void post(missionId, withId) },
    });
    return null;
  }
}

// per-session press state (module scope: one MARK button is active at a time)
let issued: { missionId: Id | null; n: number } = { missionId: null, n: 0 };
let lastPress: { at: number; hero: boolean; pending: Promise<Marker | null> } | null = null;

const perfNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Returns `mark(pressT?)`: call it from the press handler (pointerdown), passing the press time if taken earlier. */
export function useMark(getContext: () => MarkContext) {
  const missionId = useMission((s) => s.missionId);
  return useCallback(
    async (pressT?: EpochMs) => {
      if (!missionId) return null;
      const t = Math.round(pressT ?? serverNow());
      const at = perfNow();
      const mission = useMission.getState().bundle?.mission;
      const tc = mission ? timeOfDayTimecode(t, mission.frameRate, { timezone: mission.timezone }).text : formatUtc(t);
      const fb = useMarkFeedback.getState();
      const action = classifyPress(lastPress?.at ?? null, lastPress?.hero ?? false, at);

      if (action.kind === 'hero' && lastPress) {
        // double press → the previous mark becomes a Hero mark (no second marker)
        const prev = lastPress;
        prev.hero = true;
        haptic([30, 60, 30]);
        const now = Date.now();
        fb.patch({ hero: true, flashUntil: now + MARK_FLASH_MS, undoUntil: now + UNDO_WINDOW_MS, seq: (fb.last?.seq ?? 0) + 1 });
        const m = await prev.pending;
        if (!m) return null;
        try {
          const upd = await api.markers.update(missionId, m.id, { category: 'hero' });
          upsertMarkerLocally(upd);
          return upd;
        } catch (e) {
          toast({ title: `Could not make ${m.label} a Hero mark`, body: errorMessage(e), tone: 'alert' });
          return m;
        }
      }

      haptic(30);
      const ctx = getContext();
      const markers = useMission.getState().record?.markers ?? [];
      // rapid taps must not both become "M-3" while the first post is in flight
      if (issued.missionId !== missionId) issued = { missionId, n: 0 };
      const n = Math.max(nextMarkNumber(markers), issued.n + 1);
      issued.n = n;
      const input: MarkerInput = {
        label: `M-${n}`,
        category: 'note',
        t,
        lat: ctx.position?.lat ?? null,
        lon: ctx.position?.lon ?? null,
        aircraftId: ctx.aircraftId,
        cameraId: ctx.cameraId,
      };
      const pending = post(missionId, input);
      lastPress = { at, hero: false, pending };
      const now = Date.now();
      fb.set({ seq: (fb.last?.seq ?? 0) + 1, label: input.label, tc, hero: false, flashUntil: now + MARK_FLASH_MS, undoUntil: now + UNDO_WINDOW_MS, pending });
      const m = await pending;
      if (!m && useMarkFeedback.getState().last?.pending === pending) fb.set(null);
      return m;
    },
    [missionId, getContext],
  );
}

/** Undo the last mark (within the 5 s window): removes it from the mission. */
export async function undoLastMark(): Promise<void> {
  const fb = useMarkFeedback.getState();
  const last = fb.last;
  if (!last) return;
  fb.set(null);
  lastPress = null;
  const m = await last.pending;
  const missionId = useMission.getState().missionId;
  if (!m || !missionId) return;
  try {
    await api.markers.remove(missionId, m.id);
    useMission.getState().ingest([{ type: 'marker', op: 'delete', data: m }]);
  } catch (e) {
    toast({ title: `Could not undo ${m.label}`, body: errorMessage(e), tone: 'alert' });
  }
}
